import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Tooltip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
} from '@mui/material';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import { useSelector } from 'react-redux';
import { PRIMARY } from '../theme';
import { isoFromDMY } from '../utils/format';
import { card, solidBtn, ghostBtn, field, PageHeader, EmptyState } from '../components/uiKit';
import {
    INK, MUTED, BODY, LINE, HOVER, RADIUS, AMBER, GREEN,
    Label, StatusPill, MissingTag, PunchTimes, Reason, Segmented,
    missingOf, needsIn, needsOut, pickList, dayParts,
} from '../components/LeaveAttendanceComps/punchUi';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import { GetMissedPunches, PostPunchRequest, GetMyPunchRequests } from '../Api/Api';

// Day number over month reads faster in a card than one long date string.
const dayBadge = (d) => {
    const iso = isoFromDMY(d);
    const t = iso ? new Date(iso) : null;
    if (!t || Number.isNaN(t.getTime())) return { day: '--', month: '', weekday: '' };
    return {
        day: String(t.getDate()).padStart(2, '0'),
        month: t.toLocaleDateString('en-GB', { month: 'short' }),
        weekday: t.toLocaleDateString('en-GB', { weekday: 'long' }),
    };
};

const normalizeMissed = (it) => ({
    date: it.date ?? it.punchDate ?? '',
    missingType: String(it.missingType ?? it.type ?? 'both').toLowerCase(),
    checkIn: it.checkIn ?? it.loginTime ?? '',
    checkOut: it.checkOut ?? it.logoutTime ?? '',
});

const normalizeMine = (it) => ({
    id: it.id ?? it.punchRequestId ?? it.requestId,
    date: it.date ?? it.punchDate ?? it.attendanceDate ?? '',
    missingType: String(it.missingType ?? it.type ?? '').toLowerCase(),
    loginTime: it.loginTime ?? it.requestedLoginTime ?? '',
    logoutTime: it.logoutTime ?? it.requestedLogoutTime ?? '',
    reason: it.reason ?? '',
    status: String(it.status ?? 'pending').toLowerCase(),
    actionedBy: it.actionedByName ?? it.approvedByName ?? it.actionedBy ?? '',
    comment: it.comment ?? it.actionComment ?? it.remarks ?? '',
});

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
];

function DateBadge({ date, tone = AMBER, size = 56 }) {
    const b = dayBadge(date);
    return (
        <Box
            sx={{
                width: size, height: size, borderRadius: '12px', flexShrink: 0,
                bgcolor: `${tone}0F`, border: `1px solid ${tone}2E`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            }}
        >
            <Typography sx={{ fontSize: size * 0.36, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums' }}>{b.day}</Typography>
            <Typography sx={{ fontSize: size * 0.16, fontWeight: 700, color: tone, textTransform: 'uppercase', letterSpacing: 0.6, mt: 0.3 }}>{b.month}</Typography>
        </Box>
    );
}

function MissedDayCard({ item, onRaise }) {
    const b = dayBadge(item.date);
    const nothingOnRecord = !item.checkIn && !item.checkOut;

    return (
        <Box
            sx={{
                ...card, p: 1.8, display: 'flex', flexDirection: 'column', gap: 1.5,
                borderColor: '#F1E3D0',
                transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 26px -16px rgba(16,24,40,0.30)', borderColor: '#EBCFAE' },
            }}
        >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <DateBadge date={item.date} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{b.weekday || 'Missed day'}</Typography>
                    <MissingTag type={item.missingType} />
                </Box>
            </Stack>

            <Box sx={{ borderTop: `1px solid ${LINE}`, pt: 1.3 }}>
                <Label sx={{ mb: 0.6 }}>On record</Label>
                {nothingOnRecord
                    ? <Typography sx={{ fontSize: 13, color: '#B6BDC9' }}>Neither punch was recorded</Typography>
                    : <PunchTimes inTime={item.checkIn} outTime={item.checkOut} />}
            </Box>

            <Button
                onClick={() => onRaise(item)}
                startIcon={<EditCalendarRoundedIcon sx={{ fontSize: 17 }} />}
                sx={{ ...solidBtn, height: 38, fontSize: 12.5, mt: 'auto' }}
            >
                Raise correction
            </Button>
        </Box>
    );
}

function RequestRow({ item, last }) {
    const b = dayBadge(item.date);
    return (
        <Stack
            direction="row"
            spacing={1.8}
            sx={{ alignItems: 'flex-start', px: 2, py: 1.8, borderBottom: last ? 'none' : `1px solid ${LINE}`, '&:hover': { bgcolor: HOVER } }}
        >
            <DateBadge date={item.date} tone={PRIMARY} size={46} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.7 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{b.weekday}</Typography>
                    <PunchTimes inTime={item.loginTime} outTime={item.logoutTime} />
                </Stack>

                <Reason text={item.reason} sx={{ fontSize: 12.5 }} />

                {item.comment && (
                    <Box sx={{ mt: 1, px: 1.3, py: 1, borderRadius: RADIUS, bgcolor: '#F8FAFC', border: `1px solid ${LINE}` }}>
                        <Label sx={{ fontSize: 9.5 }}>{item.actionedBy ? `Reply from ${item.actionedBy}` : 'Reply'}</Label>
                        <Typography sx={{ fontSize: 12.5, color: BODY, mt: 0.2 }}>{item.comment}</Typography>
                    </Box>
                )}
            </Box>

            <StatusPill status={item.status} />
        </Stack>
    );
}

function SectionHead({ icon: Icon, title, count, tone = PRIMARY, children }) {
    return (
        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', mb: 1.4, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: `${tone}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 17, color: tone }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{title}</Typography>
            {count != null && (
                <Box sx={{ minWidth: 22, height: 22, px: 0.8, borderRadius: '999px', bgcolor: `${tone}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: tone }}>{count}</Typography>
                </Box>
            )}
            <Box sx={{ ml: 'auto' }}>{children}</Box>
        </Stack>
    );
}

export default function MissedPunchesPage() {
    // Self-service needs an employee profile; a MasterAdmin token 401s on these.
    const isMasterAdmin = useSelector(selectUserTypeId) === USER_TYPE.MASTER_ADMIN;

    const [missed, setMissed] = useState([]);
    const [mine, setMine] = useState([]);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [supported, setSupported] = useState(true);
    const [filter, setFilter] = useState('all');

    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        if (isMasterAdmin) {
            setSupported(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        const [missedRes, mineRes] = await Promise.allSettled([
            http.get(GetMissedPunches, { noAuthBounce: true }),
            http.get(GetMyPunchRequests, { params: { status: 'all' }, noAuthBounce: true }),
        ]);

        const unauthorised = [missedRes, mineRes]
            .every((r) => r.status === 'rejected' && r.reason?.response?.status === 401);
        if (unauthorised) {
            setSupported(false);
            setMissed([]); setMine([]); setCounts(null);
            setLoading(false);
            return;
        }

        // Only a 404 means "you have no missed days". Anything else failed, and
        // rendering an all-clear on a dead request would tell people their
        // attendance is fine when we simply don't know.
        let failure = '';

        if (missedRes.status === 'fulfilled') {
            const body = missedRes.value?.data;
            setMissed(body && !body.error ? pickList(body.data).map(normalizeMissed) : []);
        } else {
            setMissed([]);
            const code = missedRes.reason?.response?.status;
            if (code !== 404 && code !== 401) {
                failure = apiErrorMessage(missedRes.reason, 'Could not check your missed punches.');
            }
        }

        if (mineRes.status === 'fulfilled') {
            const body = mineRes.value?.data;
            setMine(body && !body.error ? pickList(body.data).map(normalizeMine) : []);
            setCounts(body?.data?.counts || null);
        } else {
            setMine([]); setCounts(null);
            const code = mineRes.reason?.response?.status;
            if (!failure && code !== 401) {
                failure = apiErrorMessage(mineRes.reason, 'Could not load your punch requests.');
            }
        }

        setLoadError(failure);
        setLoading(false);
    }, [isMasterAdmin]);

    useEffect(() => { load(); }, [load]);

    const openDraft = (m) => setDraft({
        ...m,
        loginTime: needsIn(m.missingType) ? '' : (m.checkIn || ''),
        logoutTime: needsOut(m.missingType) ? '' : (m.checkOut || ''),
        reason: '',
    });

    const canSubmit = Boolean(draft)
        && draft.reason.trim().length > 0
        && (!needsIn(draft.missingType) || draft.loginTime)
        && (!needsOut(draft.missingType) || draft.logoutTime);

    const submit = async () => {
        if (!canSubmit || saving) return;
        setSaving(true);
        try {
            const payload = { date: draft.date, reason: draft.reason.trim() };
            if (needsIn(draft.missingType)) payload.loginTime = draft.loginTime;
            if (needsOut(draft.missingType)) payload.logoutTime = draft.logoutTime;

            const { data: body } = await http.post(PostPunchRequest, payload);
            if (body?.error) throw new Error(body.message || 'Could not raise the correction.');
            notify(body?.message || `Correction sent for ${dayParts(draft.date).date} — pending approval.`);
            setDraft(null);
            setFilter('pending');
            await load();
        } catch (err) {
            const code = err?.response?.status;
            if (code === 409) {
                notify('A correction for that day has already been raised.', 'warning');
                setDraft(null);
                await load();
            } else if (code === 400) {
                notify(apiErrorMessage(err, 'That day is no longer open for a correction.'), 'warning');
                setDraft(null);
                await load();
            } else {
                notify(apiErrorMessage(err, 'Could not raise the correction.'), 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    const tally = useMemo(() => ({
        all: counts?.all ?? mine.length,
        pending: counts?.pending ?? mine.filter((r) => r.status === 'pending').length,
        approved: counts?.approved ?? mine.filter((r) => r.status === 'approved').length,
        rejected: counts?.rejected ?? mine.filter((r) => r.status === 'rejected').length,
    }), [counts, mine]);

    const shown = useMemo(
        () => (filter === 'all' ? mine : mine.filter((r) => r.status === filter)),
        [mine, filter],
    );

    if (!supported) {
        return (
            <Box sx={{ p: 2 }}>
                <PageHeader title="Missed Punches" subtitle="Raise corrections for days where a punch went missing" />
                <Box sx={{ ...card, p: 0 }}>
                    <EmptyState
                        icon={LockPersonRoundedIcon}
                        title="Not available for this account"
                        hint="Punch corrections are raised against an employee profile. Sign in with your employee account to use this screen."
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <PageHeader
                title="Missed Punches"
                subtitle="Days this month where a check-in or check-out went missing. Raise a correction and management will review it."
            >
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={load} disabled={loading} sx={{ ...ghostBtn, width: 40, height: 40 }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </PageHeader>

            {loading ? (
                <SkeletonRows count={5} />
            ) : loadError ? (
                <Alert
                    severity="error"
                    sx={{ borderRadius: '9px' }}
                    action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}
                >
                    {loadError}
                </Alert>
            ) : (
                <>
                    <Box sx={{ mb: 3 }}>
                        <SectionHead
                            icon={EventBusyRoundedIcon}
                            title="Needs your attention"
                            count={missed.length || null}
                            tone={missed.length ? AMBER : GREEN}
                        />

                        {missed.length === 0 ? (
                            <Box sx={{ ...card, p: 0, borderColor: '#C4EBD1', bgcolor: '#F8FDF9' }}>
                                <EmptyState
                                    icon={TaskAltRoundedIcon}
                                    title="You're all caught up"
                                    hint="Every working day this month has a complete check-in and check-out."
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 1.6,
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                                }}
                            >
                                {missed.map((m, i) => (
                                    <MissedDayCard key={m.date || i} item={m} onRaise={openDraft} />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {mine.length > 0 && (
                        <Box>
                            <SectionHead icon={HistoryRoundedIcon} title="Your requests" count={tally.all}>
                                <Segmented
                                    items={FILTERS.map((f) => ({ ...f, count: tally[f.key] }))}
                                    value={filter}
                                    onChange={setFilter}
                                />
                            </SectionHead>

                            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                                {shown.length === 0 ? (
                                    <EmptyState
                                        icon={HistoryRoundedIcon}
                                        title={`No ${filter} requests`}
                                        hint="Switch the filter to see the rest of your corrections."
                                    />
                                ) : shown.map((r, i) => (
                                    <RequestRow key={r.id ?? i} item={r} last={i === shown.length - 1} />
                                ))}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            <Dialog open={Boolean(draft)} onClose={() => !saving && setDraft(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px' } } }}>
                <DialogTitle sx={{ pb: 1.4 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <DateBadge date={draft?.date} size={48} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: INK }}>Raise a correction</Typography>
                            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.1 }}>
                                {dayBadge(draft?.date).weekday} · {missingOf(draft?.missingType).label.toLowerCase()}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: '14px !important' }}>
                    <Stack spacing={1.8}>
                        <Stack direction="row" spacing={1.4}>
                            {needsIn(draft?.missingType) && (
                                <TextField
                                    label="Check-in time" type="time" size="small" fullWidth
                                    value={draft?.loginTime || ''}
                                    onChange={(e) => setDraft((d) => d && { ...d, loginTime: e.target.value })}
                                    slotProps={{ inputLabel: { shrink: true } }} sx={field}
                                />
                            )}
                            {needsOut(draft?.missingType) && (
                                <TextField
                                    label="Check-out time" type="time" size="small" fullWidth
                                    value={draft?.logoutTime || ''}
                                    onChange={(e) => setDraft((d) => d && { ...d, logoutTime: e.target.value })}
                                    slotProps={{ inputLabel: { shrink: true } }} sx={field}
                                />
                            )}
                        </Stack>
                        <TextField
                            label="Reason" size="small" fullWidth multiline minRows={3} required
                            placeholder="e.g. Phone battery died before check-out"
                            value={draft?.reason || ''}
                            onChange={(e) => setDraft((d) => d && { ...d, reason: e.target.value })}
                            sx={field}
                        />
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 1.4 }}>
                        Management reviews this. A punch already on record is never overwritten.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDraft(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submit} disabled={!canSubmit || saving}
                        startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{ ...solidBtn, height: 40, px: 2.4, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
                        {saving ? 'Sending…' : 'Send request'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
